/**
 * The outer shell of a bottom analysis dock: the resize grip, the height and
 * the expanded (fullscreen) layout, the palette injection, focus-on-open and
 * the Escape hook-point. Lifted from `ClearanceDock` / `StationVolumeDock`,
 * which carried two copies of all of it line for line (spec §B3).
 *
 * What it deliberately does NOT own:
 *  - **The state.** Height, expanded and "is a dock open" live in the shared
 *    dock atoms, written by the dock; the frame is told them and reports a new
 *    height through `onHeightChange`. One persisted height across both docks
 *    stays the dock's contract, not the frame's.
 *  - **The panel buttons.** ⓘ ⤢ ✕ sit in the header's flow now
 *    (`DockPanelButtons`), not absolutely positioned over the frame's corner —
 *    the corner buttons were why the header needed a `pr-16` hack.
 *  - **What Escape does.** The brush-then-dock peel is dock state; the frame
 *    only guarantees the key came from inside its own DOM (see `onKeyDown`).
 */
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode, type Ref } from 'react';

import { DOCK_HEIGHT_MAX, DOCK_HEIGHT_MIN } from '@/atoms/stationVolumes.atom';
import { LoadingBadgeKeyframes } from '@/components/ui/LoadingBadge';
import { SV_TOKENS } from '@/lib/stationVolumes/tokens';
import { cn } from '@/lib/utils';

export interface DockFrameProps {
  /** The root's `data-testid` — `clearance-dock`, `station-volume-dock`. */
  testId: string;
  /**
   * The docks' own test-id prefix, `clearance` or `sv`. The grip is
   * `${prefix}-resize`, the id every existing resize test drives.
   */
  testIdPrefix: string;
  /** The region's accessible name — the dock's title. */
  ariaLabel: string;
  /** The current (persisted) height, px. Ignored while `expanded`. */
  height: number;
  /** A new height from a drag or an arrow key, already clamped. */
  onHeightChange: (height: number) => void;
  minHeight?: number;
  maxHeight?: number;
  /** Arrow-key resize step. The two docks had 24 and 20; 20 is the default. */
  resizeKeyStep?: number;
  /** The grip's accessible name and its tooltip (the keys it answers to). */
  resizeLabel: string;
  resizeHelp: string;
  /**
   * Fullscreen: the frame fills the viewer region (`absolute inset-0`), drops
   * the grip and the inline height. The viewer root is itself inset below the
   * top bar and right of the left sidebar, so there is no offset arithmetic.
   */
  expanded: boolean;
  /**
   * Escape pressed INSIDE the frame's own DOM. React bubbles a portalled
   * menu's keydown through this element too, and an Escape that closes the
   * Export menu must not also close the dock under it — so a key whose target
   * is not a DOM descendant never reaches this. The event is already
   * `stopPropagation`ed. Omit it and Escape passes through untouched.
   */
  onEscape?: () => void;
  /**
   * Focus the frame whenever this changes to a non-null value — pass the open
   * run's id (and gate it on the metadata being in hand when the dock renders
   * its content a render later). Escape has to work on the FIRST press, and a
   * screen-reader user has to land inside the thing that just appeared. The
   * FRAME takes focus, not the active tab: focusing a tab painted a focus ring
   * on every open, which read as a stuck selection (audit C9).
   */
  focusKey?: string | null;
  /**
   * Ends a grip drag in flight whenever it changes — the open run. If the slot
   * moved on under a drag (a volumes card took it), the drag's next
   * `pointermove` would otherwise write the dock straight back open.
   * Unmount always ends it.
   */
  dragResetKey?: unknown;
  /** The root element, for a dock that measures itself. */
  ref?: Ref<HTMLDivElement>;
  className?: string;
  children: ReactNode;
}

const clamp = (h: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, h));

/**
 * The share of the viewport a dock may take, so the canvas keeps some room on a
 * short (landscape-phone) screen. 0.75 leaves desktop alone: any window taller
 * than ~750 px still reaches `DOCK_HEIGHT_MAX` (560).
 */
const DOCK_VIEWPORT_MAX_FRACTION = 0.75;
/** Phones (the viewer's `useIsMobile` breakpoint): the app header and the
 *  canvas overlays need more than a quarter of a portrait screen. */
const DOCK_VIEWPORT_MAX_FRACTION_NARROW = 0.55;
const NARROW_VIEWPORT_PX = 768;
const isNarrowViewport = () => typeof window !== 'undefined' && window.innerWidth < NARROW_VIEWPORT_PX;
const viewportMaxFraction = () => (isNarrowViewport() ? DOCK_VIEWPORT_MAX_FRACTION_NARROW : DOCK_VIEWPORT_MAX_FRACTION);
const viewportMaxHeight = () =>
  typeof window === 'undefined' ? Infinity : Math.floor(window.innerHeight * viewportMaxFraction());

export const DockFrame = ({
  testId,
  testIdPrefix,
  ariaLabel,
  height,
  onHeightChange,
  minHeight = DOCK_HEIGHT_MIN,
  maxHeight = DOCK_HEIGHT_MAX,
  resizeKeyStep = 20,
  resizeLabel,
  resizeHelp,
  expanded,
  onEscape,
  focusKey = null,
  dragResetKey,
  ref,
  className,
  children,
}: DockFrameProps) => {
  const frameRef = useRef<HTMLDivElement | null>(null);
  /** A grip drag in flight — switches the height transition off (below). */
  const [resizing, setResizing] = useState(false);
  /** One AbortController owns a drag's window listeners, so a dock closed or
   *  unmounted mid-drag (Escape, the card's toggle) cannot leave a
   *  `pointermove` writing a height into a dock that is gone. */
  const dragAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => dragAbortRef.current?.abort(), []);
  useEffect(() => {
    dragAbortRef.current?.abort();
    dragAbortRef.current = null;
    setResizing(false);
  }, [dragResetKey]);

  useEffect(() => {
    if (focusKey != null) frameRef.current?.focus({ preventScroll: true });
  }, [focusKey]);

  // A commit-time ref write (the React Compiler's rule permits it), merging
  // the frame's own handle with the caller's.
  const setRoot = (el: HTMLDivElement | null) => {
    frameRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  const startResize = (e: PointerEvent<HTMLDivElement>) => {
    const startY = e.clientY;
    const startH = Math.min(height, viewportMaxHeight());
    // Keep the gesture on the grip even when the finger/pointer leaves it.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Synthetic events (tests) carry no active pointer.
    }
    // The callback is the one in hand when the drag STARTED — the same as the
    // docks' old `base` snapshot. A drag is one gesture on one dock.
    const report = onHeightChange;
    // `sv-anim` transitions the height over 160 ms, which is right for a
    // keyboard step or an expand and wrong for a drag: every pointermove
    // restarted the transition and the dock trailed the pointer in steps.
    // `data-resizing` switches it off (`.sv-anim[data-resizing='true']`).
    setResizing(true);
    dragAbortRef.current?.abort();
    const ac = new AbortController();
    dragAbortRef.current = ac;
    const move = (ev: globalThis.PointerEvent) =>
      report(clamp(startH + (startY - ev.clientY), minHeight, Math.max(minHeight, Math.min(maxHeight, viewportMaxHeight()))));
    const up = () => {
      setResizing(false);
      ac.abort();
      if (dragAbortRef.current === ac) dragAbortRef.current = null;
    };
    window.addEventListener('pointermove', move, { signal: ac.signal });
    window.addEventListener('pointerup', up, { signal: ac.signal });
    window.addEventListener('pointercancel', up, { signal: ac.signal });
  };

  const resizeKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === 'ArrowUp' ? resizeKeyStep : e.key === 'ArrowDown' ? -resizeKeyStep : 0;
    if (!delta) return;
    e.preventDefault();
    onHeightChange(clamp(height + delta, minHeight, maxHeight));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' || !onEscape) return;
    if (!(e.target instanceof Node) || !e.currentTarget.contains(e.target)) return;
    e.stopPropagation();
    onEscape();
  };

  return (
    <div
      ref={setRoot}
      data-testid={testId}
      role='region'
      aria-label={ariaLabel}
      // Focusable but not in the tab order: the open-focus target, and the
      // element whose keydown carries Escape. `outline-none` because a
      // programmatic focus on a whole panel is not a place the reader tabbed to.
      tabIndex={-1}
      onKeyDown={onKeyDown}
      data-resizing={resizing ? 'true' : 'false'}
      data-expanded={expanded ? 'true' : 'false'}
      // `sv-root` is what SV_TOKENS scopes its custom properties to, and where
      // the background comes from (`.sv-root{background:var(--sv-surface)}`),
      // which the forced-colours block can repaint — a `bg-white` here would
      // win over it. Text size and ink are set ONCE here so no control below
      // needs its own `text-xs`.
      //
      // `relative shrink-0`, not `absolute bottom-0`: the dock is a flex child
      // of the viewer column, so the canvas above shrinks by exactly this
      // height and bottom-anchored overlays stay where they were drawn.
      className={cn(
        'sv-root sv-anim pointer-events-auto z-20 flex w-full flex-col border-t border-slate-200 text-xs text-slate-700 shadow-lg outline-none dark:border-stone-700 dark:text-stone-200',
        expanded ? 'absolute inset-0 z-30 h-auto' : 'relative shrink-0',
        className,
      )}
      // Expanded, the height is the parent's: `inset-0` fixes all four edges
      // and an inline height would fight it.
      // `maxHeight`: the stored height is a desktop number (up to 560 px); on a
      // phone, or a landscape phone ~390 px tall, it must still leave canvas.
      style={expanded ? undefined : { height, maxHeight: `${viewportMaxFraction() * 100}dvh` }}
    >
      <style>{SV_TOKENS}</style>
      {/* Injected once per overlay container, as `LoadingBadge`'s contract
          requires — the indeterminate bar's animation lives in it. */}
      <LoadingBadgeKeyframes />

      {/* The grip. The frame's own 1 px `border-t` is the visible line; this is
          a 20 px hit target straddling it — 12 px over the canvas, 8 px into
          the dock, so it stops short of the header's 28 px controls, which
          start 6 px down — with a 32×4 pill that appears on hover, on keyboard
          focus and during a drag. It takes no layout height, so it costs the
          body nothing (the old 8 px grip strip was a row of the body's). No grip while
          expanded: there is nothing left to resize, and the top edge is then
          the viewer region's, under the app's own chrome. */}
      {!expanded && (
        <div
          data-testid={`${testIdPrefix}-resize`}
          role='separator'
          aria-orientation='horizontal'
          aria-label={resizeLabel}
          aria-valuenow={height}
          aria-valuemin={minHeight}
          aria-valuemax={maxHeight}
          title={resizeHelp}
          tabIndex={0}
          onPointerDown={startResize}
          onKeyDown={resizeKeys}
          // `touch-none` or iOS claims the drag as a scroll and cancels it; on
          // touch the target grows upward (over the canvas, never further into
          // the header) and the pill stays visible, since there is no hover.
          className='group absolute inset-x-0 -top-3 z-30 h-5 cursor-ns-resize touch-none outline-none pointer-coarse:-top-6 pointer-coarse:h-8'
        >
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute top-3 left-1/2 h-1 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-300 transition-opacity group-hover:opacity-100 group-focus-visible:w-12 group-focus-visible:bg-sky-500 group-focus-visible:opacity-100 dark:bg-stone-600',
              resizing ? 'opacity-100' : 'opacity-0 pointer-coarse:opacity-100',
              'pointer-coarse:top-6',
            )}
          />
        </div>
      )}
      {children}
    </div>
  );
};

/**
 * The body region under the tab bar: the tab panel. `overflow-hidden`, not
 * `auto` — the docks' rule: the chart is sized from the measured box, so it
 * always fits, and content that genuinely does not (the table) scrolls in its
 * OWN container so the chart can never be pushed out of view.
 */
export interface DockBodyProps {
  /** `${prefix}-tabpanel` — the id `DockTabBar`'s tabs point `aria-controls` at. */
  id: string;
  /** The active tab's id, `${prefix}-tab-${tab}`. */
  labelledBy: string;
  ref?: Ref<HTMLDivElement>;
  className?: string;
  children: ReactNode;
}

export const DockBody = ({ id, labelledBy, ref, className, children }: DockBodyProps) => (
  <div
    ref={ref}
    id={id}
    role='tabpanel'
    aria-labelledby={labelledBy}
    className={cn('flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-2', className)}
  >
    {children}
  </div>
);
